import { useState } from "react";
import { useAttributeTestResultDetail } from "../../../../hooks/audit/useAttributeTestResultDetail";
import { getTrailConclusionDisplay } from "../lib/trailConclusion";
import { ConclusionHoverCard } from "./ConclusionHoverCard";

interface AttributeTestConclusionCardProps {
  /** Procedure-session id used to scope the lazy detail fetch/cache key. */
  procedureSessionId: string;
  /** Bare attribute-test-result id (no `attr-test-id-` prefix). */
  attributeTestResultId: string;
}

/**
 * The conclusion affordance on an attribute-test cell.
 *
 * The heavy detail is fetched lazily on first open via
 * `useAttributeTestResultDetail` — the badge cell only ships the slim summary,
 * which does not carry the conclusion text.
 */
export const AttributeTestConclusionCard = ({
  procedureSessionId,
  attributeTestResultId,
}: AttributeTestConclusionCardProps) => {
  // Keep the fetch enabled after the first open so re-opening reads from cache
  // instantly instead of re-triggering the request.
  const [hasOpened, setHasOpened] = useState(false);

  const { data: result, isLoading } = useAttributeTestResultDetail(
    procedureSessionId,
    hasOpened ? attributeTestResultId : undefined,
  );

  // The Workpaper Review panel's Conclusion block reads this same helper, so the
  // card and the panel cannot word one result two ways.
  const display = result
    ? getTrailConclusionDisplay({
        inconclusive: !result.isConclusive,
        l1: result.auditorTrail?.l1 ?? "",
      })
    : null;

  return (
    <ConclusionHoverCard
      onOpen={() => setHasOpened(true)}
      isLoading={isLoading}
      conclusion={
        display?.kind === "inconclusive"
          ? display.message
          : display?.kind === "authored"
            ? display.text
            : null
      }
      emptyMessage="No conclusion available for this result."
    />
  );
};
